import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAdminStats, getAdminUsers, updateAdminUser, deleteAdminUser,
  getAdminPlans, createAdminUser, resetAdminPassword,
} from '../api/admin';
import { Link } from 'react-router-dom';

export default function AdminPage() {
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
        <Link to="/admin/settings" className="btn-secondary text-sm">Site Settings →</Link>
      </div>
      <StatsSection />
      <UsersSection />
    </div>
  );
}

// ── Stats ─────────────────────────────────────────────────────────────────────
function StatsSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn:  () => getAdminStats().then((r) => r.data),
  });

  if (isLoading) return <div className="h-24 flex items-center justify-center"><Spinner /></div>;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <StatCard label="Total users"  value={data.total_users} />
      <StatCard label="Active users" value={data.active_users} />
      <StatCard label="Total images" value={data.total_images} />
      <StatCard label="Storage used" value={`${(data.total_storage_mb || 0).toFixed(1)} MB`} />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="card p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-white mt-1">{value}</p>
    </div>
  );
}

// ── Users table ───────────────────────────────────────────────────────────────
function UsersSection() {
  const qc                    = useQueryClient();
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showCreate, setShowCreate]   = useState(false);
  const [resetUser, setResetUser]     = useState(null); // user object for reset modal

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', { page, search }],
    queryFn:  () => getAdminUsers({ page, limit: 20, search }).then((r) => r.data),
    keepPreviousData: true,
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn:  () => getAdminPlans().then((r) => r.data),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, ...patch }) => updateAdminUser(id, patch),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['admin'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => deleteAdminUser(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['admin'] }),
  });

  const users = data?.data ?? [];
  const meta  = data?.meta ?? { total: 0, pages: 1 };

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <h2 className="font-semibold text-white">Users <span className="text-gray-500 text-sm font-normal">({meta.total})</span></h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <form
            className="flex gap-2"
            onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); setPage(1); }}
          >
            <input
              className="input w-48 text-sm"
              placeholder="Search email or name…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn-secondary text-sm px-3">Search</button>
            {search && (
              <button
                type="button"
                className="btn-secondary text-sm px-3"
                onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
              >
                Clear
              </button>
            )}
          </form>
          <button
            className="btn-primary text-sm px-4"
            onClick={() => setShowCreate(true)}
          >
            + Create User
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="p-12 flex justify-center"><Spinner /></div>
      ) : users.length === 0 ? (
        <div className="p-12 text-center text-gray-500">No users found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400 text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Plan</th>
                <th className="px-4 py-3 text-right">Storage</th>
                <th className="px-4 py-3 text-center">Active</th>
                <th className="px-4 py-3 text-center">Admin</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-white">{u.display_name}</p>
                    <p className="text-gray-500 text-xs">{u.email}</p>
                    <p className="text-gray-600 text-xs">Joined {new Date(u.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      className="input py-1 text-xs w-28"
                      value={u.plan_id}
                      onChange={(e) => updateMut.mutate({ id: u.id, plan_id: parseInt(e.target.value, 10) })}
                    >
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-300">
                    {(u.storage_used_mb || 0).toFixed(1)} MB
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Toggle
                      value={!!u.is_active}
                      onChange={(v) => updateMut.mutate({ id: u.id, is_active: v })}
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Toggle
                      value={!!u.is_admin}
                      onChange={(v) => updateMut.mutate({ id: u.id, is_admin: v })}
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        className="text-blue-400 hover:text-blue-300 text-xs transition-colors"
                        onClick={() => setResetUser(u)}
                      >
                        Reset pwd
                      </button>
                      <button
                        className="text-red-400 hover:text-red-300 text-xs transition-colors"
                        onClick={() => {
                          if (confirm(`Delete user "${u.email}"? This cannot be undone.`)) {
                            deleteMut.mutate(u.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {meta.pages > 1 && (
        <div className="p-4 border-t border-gray-800 flex items-center justify-between">
          <p className="text-gray-500 text-xs">{meta.total} users</p>
          <div className="flex gap-2">
            <button className="btn-secondary text-xs px-3 py-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
            <span className="text-gray-400 text-xs self-center">Page {page} / {meta.pages}</span>
            <button className="btn-secondary text-xs px-3 py-1" disabled={page >= meta.pages} onClick={() => setPage((p) => p + 1)}>Next →</button>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreate && (
        <CreateUserModal
          plans={plans}
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['admin'] }); }}
        />
      )}

      {/* Reset Password Modal */}
      {resetUser && (
        <ResetPasswordModal
          user={resetUser}
          onClose={() => setResetUser(null)}
        />
      )}
    </div>
  );
}

// ── Create User Modal ─────────────────────────────────────────────────────────
function CreateUserModal({ plans, onClose, onSuccess }) {
  const [form, setForm] = useState({
    display_name: '',
    email: '',
    password: '',
    plan_id: plans[0]?.id ?? 1,
    is_admin: false,
  });
  const [error, setError] = useState('');

  const createMut = useMutation({
    mutationFn: (data) => createAdminUser(data),
    onSuccess:  () => onSuccess(),
    onError:    (e) => setError(e.response?.data?.error || 'Error creating user.'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!form.display_name || !form.email || !form.password) {
      return setError('All fields are required.');
    }
    createMut.mutate(form);
  };

  return (
    <Modal title="Create User" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <p className="text-red-400 text-sm bg-red-900/20 p-3 rounded-lg">{error}</p>}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Display Name</label>
          <input
            className="input w-full"
            placeholder="John Doe"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Email</label>
          <input
            className="input w-full"
            type="email"
            placeholder="john@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Password</label>
          <input
            className="input w-full"
            type="password"
            placeholder="Min. 6 characters"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Plan</label>
          <select
            className="input w-full"
            value={form.plan_id}
            onChange={(e) => setForm({ ...form, plan_id: parseInt(e.target.value, 10) })}
          >
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <Toggle value={form.is_admin} onChange={(v) => setForm({ ...form, is_admin: v })} />
          <span className="text-sm text-gray-400">Admin privileges</span>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="btn-primary flex-1" disabled={createMut.isPending}>
            {createMut.isPending ? 'Creating…' : 'Create User'}
          </button>
          <button type="button" className="btn-secondary flex-1" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

// ── Reset Password Modal ──────────────────────────────────────────────────────
function ResetPasswordModal({ user, onClose }) {
  const [password, setPassword]   = useState('');
  const [confirm,  setConfirm]    = useState('');
  const [error,    setError]      = useState('');
  const [success,  setSuccess]    = useState(false);

  const resetMut = useMutation({
    mutationFn: (data) => resetAdminPassword(user.id, data),
    onSuccess:  () => setSuccess(true),
    onError:    (e) => setError(e.response?.data?.error || 'Error resetting password.'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    resetMut.mutate({ password });
  };

  return (
    <Modal title="Reset Password" onClose={onClose}>
      {success ? (
        <div className="space-y-4 text-center">
          <p className="text-green-400">Password reset successfully for <strong>{user.email}</strong></p>
          <button className="btn-primary w-full" onClick={onClose}>Close</button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-gray-400 text-sm">Resetting password for <strong className="text-white">{user.email}</strong></p>
          {error && <p className="text-red-400 text-sm bg-red-900/20 p-3 rounded-lg">{error}</p>}
          <div>
            <label className="block text-sm text-gray-400 mb-1">New Password</label>
            <input
              className="input w-full"
              type="password"
              placeholder="Min. 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Confirm Password</label>
            <input
              className="input w-full"
              type="password"
              placeholder="Repeat password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={resetMut.isPending}>
              {resetMut.isPending ? 'Resetting…' : 'Reset Password'}
            </button>
            <button type="button" className="btn-secondary flex-1" onClick={onClose}>Cancel</button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// ── Modal wrapper ─────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h3 className="font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none">&times;</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-9 h-5 rounded-full transition-colors relative ${value ? 'bg-brand-600' : 'bg-gray-700'}`}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}

function Spinner() {
  return <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />;
}
